/**
 * Smart logging utility that automatically disables logs in production builds.
 *
 * Usage:
 *   import logger from '@/utils/logger';
 *
 *   logger.log('Debug info');           // Only in development
 *   logger.error('Error occurred', err); // Always logged
 *   logger.warn('Warning message');     // Always logged
 *   logger.info('Info message');        // Only in development
 *
 * In production builds, only error() and warn() will output.
 * In development, all log levels work.
 */

const isProduction = process.env.NODE_ENV === 'production';

/**
 * Logger that suppresses debug/info logs in production
 */
const logger = {
    /**
     * Debug logging - suppressed in production
     * Use for verbose debugging information
     */
    log: (...args: any[]) => {
        if (!isProduction) {
            console.log(...args);
        }
    },

    /**
     * Debug logging - suppressed in production
     * Use for detailed debugging information
     */
    debug: (...args: any[]) => {
        if (!isProduction) {
            console.debug(...args);
        }
    },

    /**
     * Info logging - suppressed in production
     * Use for informational messages
     */
    info: (...args: any[]) => {
        if (!isProduction) {
            console.info(...args);
        }
    },

    /**
     * Warning logging - always active
     * Use for warnings that should be visible in production
     */
    warn: (...args: any[]) => {
        console.warn(...args);
    },

    /**
     * Error logging - always active
     * Use for errors that should be visible in production
     */
    error: (...args: any[]) => {
        console.error(...args);
    },

    /**
     * Table logging - suppressed in production
     * Use for tabular data display in development
     */
    table: (data: any) => {
        if (!isProduction) {
            console.table(data);
        }
    },

    /**
     * Group logging - suppressed in production
     * Use for grouped console output in development
     */
    group: (label: string) => {
        if (!isProduction) {
            console.group(label);
        }
    },

    groupEnd: () => {
        if (!isProduction) {
            console.groupEnd();
        }
    },
};

export default logger;
